/**
 * useUserManagement Hook
 * Custom hook for user management operations with object-based permissions
 *
 * Reads go through a shared, tenant-scoped query cache (query-cache.util.ts):
 * the User Management tabs unmount when hidden and each owns its own instance
 * of this hook, so without it every tab switch re-downloaded users, roles,
 * invites and modules. Fresh data is reused; mutations patch or invalidate
 * exactly what they changed instead of re-reading whole lists; the page's
 * refresh button calls `invalidateUserManagementCache()` to force a re-read.
 */

import { useState, useCallback, useRef } from 'react';
import userManagementService, {
  User,
  Role,
  DisplayModulePermission,
  UpdateUserData,
  CreateRoleData,
  UpdateRoleData,
  Module,
} from '@/services/user-management.service';
import * as inviteApi from '@/services/invite.api';
import type { AdminInvite, CreateInvitePayload } from '@/services/invite.api';
import { showPlanLockIfTagged } from '@/utils/plan-lock.util';
import { createQueryCache } from '@/utils/query-cache.util';

/** Query for a single page of users. Every field is optional, so callers that
 *  don't paginate (Hierarchy, Reports) keep working unchanged. */
export interface LoadUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  isActive?: boolean;
}

type UsersPage = Awaited<ReturnType<typeof userManagementService.getUsers>>;
type RolePermissions = { roleId: string; roleName: string; permissions: DisplayModulePermission[] };
type LoadOpts = { force?: boolean };

/** Users/roles/invites change only when an admin acts — and this client's own
 *  actions update the cache directly. 5 min bounds how long another admin's
 *  change can go unseen without pressing refresh. */
const cache = createQueryCache(5 * 60 * 1000);
const DIRECTORY_LIMIT = 500;

const K = {
  users: (p?: LoadUsersParams) => `users:${JSON.stringify(p ?? {})}`,
  usersAll: 'users:',
  directory: 'directory',
  roles: 'roles',
  roleHierarchy: 'roleHierarchy',
  rolePerms: (id: string) => `rolePerms:${id}`,
  modules: 'modules',
  invites: 'invites',
};

/** Drop every cached user-management read — the page's refresh button. */
export const invalidateUserManagementCache = () => cache.invalidate();

const errMsg = (err: any, fallback: string) => err?.response?.data?.error?.message || fallback;

export const useUserManagement = () => {
  const [users, setUsers] = useState<User[]>([]);
  const usersRef = useRef(users);
  usersRef.current = users;
  /** Server-reported match count for the last `loadUsers` query — what a pager
   *  needs, and unknowable from `users.length` once a page is capped. */
  const [usersTotal, setUsersTotal] = useState(0);
  /** Network fetch of the users page in progress (cache hits never set it) —
   *  separate from `loading` so an unrelated action doesn't blank the table. */
  const [usersLoading, setUsersLoading] = useState(false);
  // The params of the last user read. Mutations refetch with them so a change
  // keeps the caller on the page (and filter) they were on.
  const lastUsersParams = useRef<LoadUsersParams | undefined>(undefined);
  const [roles, setRoles] = useState<Role[]>([]);
  const [invites, setInvites] = useState<AdminInvite[]>([]);
  const [roleHierarchy, setRoleHierarchy] = useState<Role[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [rolePermissions, setRolePermissions] = useState<RolePermissions | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  /**
   * Cached read. A fresh cache hit is applied immediately with no loading state
   * at all (no spinner flash on a tab switch); only a real network fetch
   * toggles `onLoading`.
   */
  const read = useCallback(
    async <T,>(
      key: string,
      fetcher: () => Promise<T>,
      apply: (data: T) => void,
      failure: string,
      opts: LoadOpts = {},
      onLoading: (v: boolean) => void = setLoading
    ): Promise<T> => {
      const hit = opts.force ? undefined : cache.peek<T>(key);
      if (hit !== undefined) {
        apply(hit);
        return hit;
      }
      onLoading(true);
      try {
        const data = await cache.get(key, fetcher, opts);
        apply(data);
        return data;
      } catch (err: any) {
        setError(errMsg(err, failure));
        throw err;
      } finally {
        onLoading(false);
      }
    },
    []
  );

  /** A write: shared loading/error/success handling. */
  const write = useCallback(
    async <T,>(fn: () => Promise<T>, failure: string, opts: { planLock?: boolean } = {}): Promise<T> => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
        return await fn();
      } catch (err: any) {
        if (!(opts.planLock && showPlanLockIfTagged(err))) setError(errMsg(err, failure));
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // ── Users ────────────────────────────────────────────────────────────────

  const loadUsers = useCallback(
    async (params?: LoadUsersParams, opts?: LoadOpts) => {
      setError(null);
      lastUsersParams.current = params;
      return read<UsersPage>(
        K.users(params),
        () => userManagementService.getUsers(params || {}),
        (result) => {
          setUsers(result.data);
          setUsersTotal(result.meta?.total ?? 0);
        },
        'Failed to load users',
        opts,
        setUsersLoading
      );
    },
    [read]
  );

  /** Every member of the tenant (the "Reports To" picker) — cached, loaded on demand. */
  const loadUserDirectory = useCallback(
    (opts?: LoadOpts): Promise<User[]> =>
      cache
        .get(K.directory, () => userManagementService.getUsers({ limit: DIRECTORY_LIMIT }), opts)
        .then((r) => r.data),
    []
  );

  /** A change that can affect which rows a list shows or how they render. */
  const afterUserChange = useCallback(async () => {
    cache.invalidate(K.usersAll);
    cache.invalidate(K.directory);
    await loadUsers(lastUsersParams.current, { force: true });
  }, [loadUsers]);

  const updateUser = useCallback(
    (userId: string, userData: UpdateUserData) =>
      write(async () => {
        const result = await userManagementService.updateUser(userId, userData);
        setSuccess(result.message);
        await afterUserChange(); // role/reports-to render from populated fields — one page re-read
        return result;
      }, 'Failed to update user'),
    [write, afterUserChange]
  );

  /**
   * Optimistic: the row flips immediately and the ONE request confirms it —
   * nothing is re-downloaded. On failure the previous rows are restored and
   * the cached lists dropped, so nothing shows a status the server refused.
   */
  const setUserStatus = useCallback(async (userId: string, isActive: boolean) => {
    const flip = (u: User) => (u._id === userId ? { ...u, isActive } : u);
    const before = usersRef.current; // rows shown when the action was taken — the rollback target
    setUsers(before.map(flip));
    cache.update<UsersPage>(K.usersAll, (r) => ({ ...r, data: r.data.map(flip) }));
    cache.update<UsersPage>(K.directory, (r) => ({ ...r, data: r.data.map(flip) }));
    setError(null);
    setSuccess(null);
    try {
      const result = await userManagementService.setUserStatus(userId, isActive);
      setSuccess(result.message);
      return result;
    } catch (err: any) {
      setUsers(before);
      cache.invalidate(K.usersAll);
      cache.invalidate(K.directory);
      setError(errMsg(err, 'Failed to update user status'));
      throw err;
    }
  }, []);

  const deleteUser = useCallback(
    (userId: string) =>
      write(async () => {
        await userManagementService.deleteUser(userId);
        setSuccess('User deleted successfully');
        await afterUserChange();
      }, 'Failed to delete user'),
    [write, afterUserChange]
  );

  // ── Invites ──────────────────────────────────────────────────────────────
  // Admin-sets-password creation is gone; the invitee sets their own password
  // on acceptance (see InviteAccept.tsx).

  const loadInvites = useCallback(
    (opts?: LoadOpts) =>
      read<AdminInvite[]>(
        K.invites,
        async () => (await inviteApi.listInvites('pending')) || [],
        setInvites,
        'Failed to load pending invites',
        opts
      ),
    [read]
  );

  const createInvite = useCallback(
    (payload: CreateInvitePayload) =>
      write(
        async () => {
          const invite = await inviteApi.createInvite(payload);
          setInvites((prev) => [invite, ...prev]);
          cache.update<AdminInvite[]>(K.invites, (list) => [invite, ...list]);
          setSuccess(`Invite sent to ${payload.email}`);
          return invite;
        },
        'Failed to send invite',
        { planLock: true }
      ),
    [write]
  );

  const revokeInvite = useCallback(
    (id: string) =>
      write(async () => {
        await inviteApi.revokeInvite(id);
        const drop = (list: AdminInvite[]) => list.filter((i) => i.id !== id);
        setInvites(drop);
        cache.update<AdminInvite[]>(K.invites, drop);
        setSuccess('Invite revoked');
      }, 'Failed to revoke invite'),
    [write]
  );

  const resendInvite = useCallback(
    (id: string) =>
      write(async () => {
        await inviteApi.resendInvite(id);
        await loadInvites({ force: true }); // new expiry comes from the server
        setSuccess('Invite re-sent');
      }, 'Failed to resend invite'),
    [write, loadInvites]
  );

  // ── Roles ────────────────────────────────────────────────────────────────

  const loadRoles = useCallback(
    (opts?: LoadOpts) =>
      read<Role[]>(K.roles, () => userManagementService.getRoles(), setRoles, 'Failed to load roles', opts),
    [read]
  );

  /** Roles render inside user rows (role chip) and the hierarchy — drop those too. */
  const afterRoleChange = useCallback(async () => {
    cache.invalidate(K.roleHierarchy);
    cache.invalidate(K.usersAll);
    await loadRoles({ force: true });
  }, [loadRoles]);

  const createRole = useCallback(
    (roleData: CreateRoleData) =>
      write(async () => {
        const result = await userManagementService.createRole(roleData);
        setSuccess(result.message);
        await afterRoleChange();
        return result;
      }, 'Failed to create role'),
    [write, afterRoleChange]
  );

  const updateRole = useCallback(
    (roleId: string, roleData: UpdateRoleData) =>
      write(async () => {
        const result = await userManagementService.updateRole(roleId, roleData);
        setSuccess(result.message);
        await afterRoleChange();
        return result;
      }, 'Failed to update role'),
    [write, afterRoleChange]
  );

  const deleteRole = useCallback(
    (roleId: string) =>
      write(async () => {
        await userManagementService.deleteRole(roleId);
        cache.invalidate(K.rolePerms(roleId));
        setSuccess('Role deleted successfully');
        await afterRoleChange();
      }, 'Failed to delete role'),
    [write, afterRoleChange]
  );

  const loadRoleHierarchy = useCallback(
    (opts?: LoadOpts) =>
      read<Role[]>(
        K.roleHierarchy,
        () => userManagementService.getRoleHierarchy(),
        setRoleHierarchy,
        'Failed to load role hierarchy',
        opts
      ),
    [read]
  );

  // ── Role permissions ─────────────────────────────────────────────────────

  const loadRolePermissions = useCallback(
    (roleId: string, opts?: LoadOpts) =>
      read<RolePermissions>(
        K.rolePerms(roleId),
        () => userManagementService.getRolePermissions(roleId),
        setRolePermissions,
        'Failed to load role permissions',
        opts
      ),
    [read]
  );

  const updateRolePermissions = useCallback(
    (roleId: string, permissions: DisplayModulePermission[]) =>
      write(async () => {
        const result = await userManagementService.updateRolePermissions(roleId, permissions);
        setSuccess(result.message);
        // The roles table shows each role's module count.
        await Promise.all([loadRoles({ force: true }), loadRolePermissions(roleId, { force: true })]);
        return result;
      }, 'Failed to update role permissions'),
    [write, loadRoles, loadRolePermissions]
  );

  // ── Modules ──────────────────────────────────────────────────────────────

  const loadModules = useCallback(
    (opts?: LoadOpts) =>
      read<Module[]>(K.modules, () => userManagementService.getModules(), setModules, 'Failed to load modules', opts),
    [read]
  );

  // ── Reporting (per-user, not cached) ─────────────────────────────────────

  const getUserReportingTree = useCallback(async (userId: string) => {
    try {
      setLoading(true);
      setError(null);
      return await userManagementService.getUserReportingTree(userId);
    } catch (err: any) {
      setError(errMsg(err, 'Failed to load reporting tree'));
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearMessages = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  return {
    // State
    users,
    usersTotal,
    usersLoading,
    roles,
    roleHierarchy,
    modules,
    rolePermissions,
    invites,
    loading,
    error,
    success,
    // User methods
    loadUsers,
    loadUserDirectory,
    updateUser,
    setUserStatus,
    deleteUser,
    // Invite methods
    loadInvites,
    createInvite,
    revokeInvite,
    resendInvite,
    // Role methods
    loadRoles,
    createRole,
    updateRole,
    deleteRole,
    loadRoleHierarchy,
    // Role permission methods
    loadRolePermissions,
    updateRolePermissions,
    // Module methods
    loadModules,
    // Reporting methods
    getUserReportingTree,
    // Utility
    clearMessages,
  };
};
